import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Settings2, Landmark, Receipt, Save, Search, Loader2, CheckCircle2, AlertCircle,
  Filter, Copy, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AccountConfiguration = ({ clientId, clientName, chartType, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [spMappings, setSpMappings] = useState({});
  const [ceMappings, setCeMappings] = useState({});
  const [searchSP, setSearchSP] = useState('');
  const [searchCE, setSearchCE] = useState('');
  const [filterCE, setFilterCE] = useState('all');
  const [activeTab, setActiveTab] = useState('ce');
  // SP mode
  const [spIsProgressive, setSpIsProgressive] = useState(true);
  const [spModeLoading, setSpModeLoading] = useState(false);
  // Copy CE mappings
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [otherClients, setOtherClients] = useState([]);
  const [copySource, setCopySource] = useState('');
  const [copying, setCopying] = useState(false);

  const fetchConfig = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    try {
      const resp = await fetch(`${API}/api/clients/${clientId}/account-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setConfig(data);

        // Initialize SP mappings
        const spMap = {};
        for (const acc of data.sp_accounts) {
          spMap[acc.account_code] = acc.category || '';
        }
        setSpMappings(spMap);

        // Initialize CE mappings
        const ceMap = {};
        for (const acc of data.ce_accounts) {
          ceMap[acc.account_code] = {
            category: acc.category || '',
            subcategory: acc.subcategory || '',
          };
        }
        setCeMappings(ceMap);
      }

      // Fetch client info for sp_is_progressive and other clients
      const clientResp = await fetch(`${API}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (clientResp.ok) {
        const clients = await clientResp.json();
        const thisClient = clients.find(c => c.id === clientId);
        if (thisClient) {
          setSpIsProgressive(thisClient.sp_is_progressive === 1 || thisClient.sp_is_progressive === true);
        }
        const sameChart = clients.filter(c => c.id !== clientId && c.chart_of_accounts === (thisClient?.chart_of_accounts || chartType));
        setOtherClients(sameChart);
      }
    } catch {
      toast.error('Errore nel caricamento della configurazione');
    }
    setLoading(false);
  }, [clientId, chartType]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // Toggle SP progressive mode
  const handleSpModeToggle = async (checked) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setSpModeLoading(true);
    try {
      const resp = await fetch(`${API}/api/clients/${clientId}/sp-mode`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ sp_is_progressive: checked }),
      });
      if (resp.ok) {
        setSpIsProgressive(checked);
        toast.success(checked
          ? 'SP impostato come progressivo (saldi cumulativi)'
          : 'SP impostato come incrementale (delta mensili da sommare)'
        );
      } else {
        toast.error('Errore nel salvataggio');
      }
    } catch {
      toast.error('Errore di connessione');
    }
    setSpModeLoading(false);
  };

  // Copy CE mappings from another client
  const handleCopyCE = async () => {
    if (!copySource) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setCopying(true);
    try {
      const resp = await fetch(`${API}/api/clients/${clientId}/copy-ce-mappings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_client_id: copySource }),
      });
      if (resp.ok) {
        const result = await resp.json();
        toast.success(`Copiate ${result.copied} mappature CE (${result.skipped} gia presenti)`);
        setShowCopyDialog(false);
        setCopySource('');
        fetchConfig(); // Reload
      } else {
        const err = await resp.json();
        toast.error(err.detail || 'Errore nella copia');
      }
    } catch {
      toast.error('Errore di connessione');
    }
    setCopying(false);
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setSaving(true);

    // Build SP mappings array
    const spArr = config.sp_accounts
      .filter(a => spMappings[a.account_code])
      .map(a => ({
        account_code: a.account_code,
        account_name: a.account_name,
        category: spMappings[a.account_code],
      }));

    // Build CE mappings array
    const ceArr = config.ce_accounts
      .filter(a => ceMappings[a.account_code]?.category)
      .map(a => ({
        account_code: a.account_code,
        account_name: a.account_name,
        category: ceMappings[a.account_code].category,
        subcategory: ceMappings[a.account_code].subcategory || null,
      }));

    try {
      const resp = await fetch(`${API}/api/clients/${clientId}/account-config`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ sp_mappings: spArr, ce_mappings: ceArr }),
      });
      if (resp.ok) {
        const result = await resp.json();
        toast.success(`Salvati ${result.sp_saved} conti SP e ${result.ce_saved} conti CE`);
      } else {
        toast.error('Errore nel salvataggio');
      }
    } catch {
      toast.error('Errore di connessione');
    }
    setSaving(false);
  };

  // Computed stats
  const stats = useMemo(() => {
    if (!config) return {};
    const spTotal = config.sp_accounts.length;
    const spMapped = config.sp_accounts.filter(a => spMappings[a.account_code]).length;
    const ceTotal = config.ce_accounts.length;
    const ceMapped = config.ce_accounts.filter(a => ceMappings[a.account_code]?.category).length;
    const ceAutoMatch = config.ce_accounts.filter(a => a.auto_match).length;
    return { spTotal, spMapped, ceTotal, ceMapped, ceAutoMatch };
  }, [config, spMappings, ceMappings]);

  // Filtered lists
  const filteredSP = useMemo(() => {
    if (!config) return [];
    let list = config.sp_accounts;
    if (searchSP) {
      const q = searchSP.toLowerCase();
      list = list.filter(a =>
        a.account_code.toLowerCase().includes(q) ||
        a.account_name.toLowerCase().includes(q)
      );
    }
    return list;
  }, [config, searchSP]);

  const filteredCE = useMemo(() => {
    if (!config) return [];
    let list = config.ce_accounts;
    if (searchCE) {
      const q = searchCE.toLowerCase();
      list = list.filter(a =>
        a.account_code.toLowerCase().includes(q) ||
        a.account_name.toLowerCase().includes(q)
      );
    }
    if (filterCE === 'unmapped') {
      list = list.filter(a => !ceMappings[a.account_code]?.category);
    } else if (filterCE === 'auto_match') {
      list = list.filter(a => a.auto_match);
    } else if (filterCE !== 'all') {
      list = list.filter(a => ceMappings[a.account_code]?.category === filterCE);
    }
    return list;
  }, [config, searchCE, filterCE, ceMappings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="space-y-4" data-testid="account-configuration">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-violet-50 flex items-center justify-center">
                <Settings2 className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Configura Conti</CardTitle>
                <div className="flex items-center gap-3 mt-1">
                  <Badge variant="outline" className="text-xs">Piano: {config.chart_type}</Badge>
                  <span className="text-xs text-muted-foreground">
                    SP: {stats.spMapped}/{stats.spTotal} mappati | CE: {stats.ceMapped}/{stats.ceTotal} mappati
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {otherClients.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => setShowCopyDialog(!showCopyDialog)} data-testid="copy-ce-btn">
                  <Copy className="h-4 w-4 mr-1" /> Importa CE
                </Button>
              )}
              {onClose && (
                <Button variant="outline" size="sm" onClick={onClose}>Chiudi</Button>
              )}
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-config-btn">
                {saving
                  ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Salvataggio...</>
                  : <><Save className="h-4 w-4 mr-1" /> Salva Tutto</>
                }
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Copy CE dialog */}
          {showCopyDialog && (
            <div className="mb-4 p-3 rounded-lg border border-blue-200 bg-blue-50/50 space-y-2" data-testid="copy-ce-dialog">
              <p className="text-sm font-medium text-blue-800">Importa mappature CE da un altro cliente</p>
              <p className="text-xs text-blue-600">Verranno copiati solo i conti non ancora configurati su questo cliente.</p>
              <div className="flex items-center gap-2">
                <Select value={copySource} onValueChange={setCopySource}>
                  <SelectTrigger className="flex-1 h-9 text-sm">
                    <SelectValue placeholder="Seleziona cliente sorgente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {otherClients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={handleCopyCE} disabled={!copySource || copying} data-testid="confirm-copy-btn">
                  {copying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Copia'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowCopyDialog(false)}>Annulla</Button>
              </div>
            </div>
          )}

          {/* SP Mode Toggle */}
          <div className="mb-4 p-3 rounded-lg border bg-slate-50/50 flex items-center justify-between" data-testid="sp-mode-toggle">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-slate-500" />
              <div>
                <p className="text-sm font-medium">Modalita SP: {spIsProgressive ? 'Progressivo' : 'Incrementale'}</p>
                <p className="text-xs text-muted-foreground">
                  {spIsProgressive
                    ? 'Ogni mese ha il saldo completo cumulativo (tipico piano S1)'
                    : 'Ogni mese ha solo i movimenti del mese — vengono sommati da gennaio'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{spIsProgressive ? 'Progressivo' : 'Incrementale'}</span>
              <Switch
                checked={spIsProgressive}
                onCheckedChange={handleSpModeToggle}
                disabled={spModeLoading}
                data-testid="sp-mode-switch"
              />
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="ce" className="flex items-center gap-1.5">
                <Receipt className="h-4 w-4" />
                Conto Economico
                {stats.ceTotal > 0 && stats.ceMapped < stats.ceTotal && (
                  <Badge variant="destructive" className="h-5 px-1.5 text-[10px] ml-1">
                    {stats.ceTotal - stats.ceMapped}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="sp" className="flex items-center gap-1.5">
                <Landmark className="h-4 w-4" />
                Stato Patrimoniale
                {stats.spTotal > 0 && stats.spMapped < stats.spTotal && (
                  <Badge variant="destructive" className="h-5 px-1.5 text-[10px] ml-1">
                    {stats.spTotal - stats.spMapped}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* ── CE Tab ────────────────────── */}
            <TabsContent value="ce">
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Cerca per codice o descrizione..."
                      value={searchCE}
                      onChange={e => setSearchCE(e.target.value)}
                      className="pl-9 h-9"
                      data-testid="search-ce"
                    />
                  </div>
                  <Select value={filterCE} onValueChange={setFilterCE}>
                    <SelectTrigger className="w-[180px] h-9">
                      <Filter className="h-3.5 w-3.5 mr-1" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti ({stats.ceTotal})</SelectItem>
                      <SelectItem value="unmapped">Non configurati ({stats.ceTotal - stats.ceMapped})</SelectItem>
                      <SelectItem value="auto_match">🆕 Auto-suggeriti / Da rivedere ({stats.ceAutoMatch || 0})</SelectItem>
                      <SelectItem value="variable_cost">Costi Variabili</SelectItem>
                      <SelectItem value="fixed_cost">Costi Fissi</SelectItem>
                      <SelectItem value="revenue">Ricavi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <p className="text-xs text-muted-foreground">
                  Tutte le mappature CE sono <strong>specifiche per questo cliente</strong>. Ogni cliente ha la propria configurazione dei conti.
                  Una volta impostati, gli abbinamenti vengono mantenuti per tutti i bilanci importati.
                </p>

                <div className="rounded-lg border overflow-hidden">
                  <table className="w-full text-sm" data-testid="ce-table">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-left py-2 px-3 font-medium text-xs text-slate-600 w-[100px]">Codice</th>
                        <th className="text-left py-2 px-3 font-medium text-xs text-slate-600">Descrizione</th>
                        <th className="text-left py-2 px-3 font-medium text-xs text-slate-600 w-[200px]">Categoria</th>
                        <th className="text-left py-2 px-3 font-medium text-xs text-slate-600 w-[200px]">Sottocategoria</th>
                        <th className="text-center py-2 px-3 font-medium text-xs text-slate-600 w-[60px]">Stato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCE.map((acc) => {
                        const mapping = ceMappings[acc.account_code] || {};
                        const isFixed = mapping.category === 'fixed_cost';

                        return (
                          <tr
                            key={acc.account_code}
                            className={`border-b border-slate-100 hover:bg-slate-50 ${
                              !mapping.category ? 'bg-amber-50/50' : ''
                            }`}
                            data-testid={`ce-row-${acc.account_code}`}
                          >
                            <td className="py-1.5 px-3 font-mono text-xs text-slate-500">{acc.account_code}</td>
                            <td className="py-1.5 px-3 text-slate-800">
                              {acc.account_name}
                              {acc.auto_match && (
                                <span className="ml-2 inline-block bg-amber-100 text-amber-800 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide" title="Mappatura suggerita automaticamente dal sistema. Verifica e modifica se necessario.">
                                  🆕 Auto
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 px-3">
                              <Select
                                value={mapping.category || '__none__'}
                                onValueChange={val => {
                                  setCeMappings(prev => ({
                                    ...prev,
                                    [acc.account_code]: {
                                      ...prev[acc.account_code],
                                      category: val === '__none__' ? '' : val,
                                      subcategory: val === 'fixed_cost' ? (prev[acc.account_code]?.subcategory || 'other') : '',
                                    }
                                  }));
                                }}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Seleziona..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">-- Non configurato --</SelectItem>
                                  {Object.entries(config.ce_categories).map(([key, label]) => (
                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-1.5 px-3">
                              {isFixed && (
                                <Select
                                  value={mapping.subcategory || 'other'}
                                  onValueChange={val => {
                                    setCeMappings(prev => ({
                                      ...prev,
                                      [acc.account_code]: { ...prev[acc.account_code], subcategory: val }
                                    }));
                                  }}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Object.entries(config.fixed_cost_subcategories).map(([key, label]) => (
                                      <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="py-1.5 px-3 text-center">
                              {mapping.category ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
                              ) : (
                                <AlertCircle className="h-4 w-4 text-amber-400 mx-auto" />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {filteredCE.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center py-8 text-muted-foreground">
                            {searchCE || filterCE !== 'all' ? 'Nessun risultato' : 'Nessun conto CE trovato'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-400">
                  Totale: {filteredCE.length} conti visualizzati
                </p>
              </div>
            </TabsContent>

            {/* ── SP Tab ────────────────────── */}
            <TabsContent value="sp">
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca per codice o descrizione..."
                    value={searchSP}
                    onChange={e => setSearchSP(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="search-sp"
                  />
                </div>

                <p className="text-xs text-muted-foreground">
                  Le mappature SP sono <strong>globali</strong> per il piano {config.chart_type} — valgono per tutti i clienti con lo stesso piano dei conti.
                </p>

                {config.sp_accounts.length === 0 ? (
                  <div className="flex flex-col items-center py-12 text-center">
                    <AlertCircle className="h-10 w-10 text-amber-400 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      Nessun conto SP trovato. Clicca "Estrai dati SP" nel tab SP Progressivo per estrarre le voci patrimoniali.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm" data-testid="sp-table">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left py-2 px-3 font-medium text-xs text-slate-600 w-[100px]">Codice</th>
                          <th className="text-left py-2 px-3 font-medium text-xs text-slate-600">Descrizione</th>
                          <th className="text-left py-2 px-3 font-medium text-xs text-slate-600 w-[260px]">Categoria SP</th>
                          <th className="text-center py-2 px-3 font-medium text-xs text-slate-600 w-[60px]">Stato</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSP.map((acc) => {
                          const cat = spMappings[acc.account_code] || '';
                          return (
                            <tr
                              key={acc.account_code}
                              className={`border-b border-slate-100 hover:bg-slate-50 ${!cat ? 'bg-amber-50/50' : ''}`}
                              data-testid={`sp-row-${acc.account_code}`}
                            >
                              <td className="py-1.5 px-3 font-mono text-xs text-slate-500">{acc.account_code}</td>
                              <td className="py-1.5 px-3 text-slate-800">{acc.account_name}</td>
                              <td className="py-1.5 px-3">
                                <Select
                                  value={cat || '__none__'}
                                  onValueChange={val => {
                                    setSpMappings(prev => ({
                                      ...prev,
                                      [acc.account_code]: val === '__none__' ? '' : val,
                                    }));
                                  }}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Seleziona..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">-- Non configurato --</SelectItem>
                                    {Object.entries(config.sp_categories).map(([key, label]) => (
                                      <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="py-1.5 px-3 text-center">
                                {cat ? (
                                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
                                ) : (
                                  <AlertCircle className="h-4 w-4 text-amber-400 mx-auto" />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-xs text-slate-400">
                  {stats.spMapped}/{stats.spTotal} conti SP mappati — Globale per piano {config.chart_type}
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountConfiguration;
